<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "$lib/components/ui/card"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import type { User } from "$lib/data/type/user"
  import { APP_CODE, APP_VERSION } from "$lib/env"
  import { sleep } from "$lib/sleep"
  import { deviceStore } from "$lib/stores/device-store.svelte"
  import EyeIcon from "@lucide/svelte/icons/eye"
  import EyeOffIcon from "@lucide/svelte/icons/eye-off"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"

  const FormSchema = z.object({
    email: z.string().default(""),
    token: z.string().default(""),
    password: z.string().default(""),
    password_show: z.boolean().default(false),
    password_confirm: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  const resetEmail = $derived(page.url.searchParams.get("email") ?? "")
  const resetToken = $derived(page.url.searchParams.get("token") ?? "")

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))

  createQuery(() => ({
    queryKey: ["password-reset", resetEmail, resetToken],
    queryFn: async () => {

      await sleep(1)
      form.reset({
        ...FormEmpty,
        email: resetEmail,
        token: resetToken,
      })

      return ""
    }
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<User>>("/api/auth/password-reset", data)
      const responseData = response.data!

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (prev) => ({
          ...prev,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        return
      }

      toast.success("Password berhasil direset", {
        duration: Infinity
      })

      form.reset(FormEmpty)
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>
<main class="relative flex h-full w-full overflow-y-auto flex-col items-center justify-center">
  <div class="absolute top-4 inset-e-4 text-end font-mono text-xs font-light text-gray-800">
    <div>{APP_CODE}-{APP_VERSION}</div>
    <div>{deviceStore.device.device_id.toUpperCase().substring(0, 16)}</div>
  </div>
  <div class="relative w-85">
    <div class="absolute -top-28 w-full flex justify-center mb-4">
      <img
        src="/assets/logo.png"
        alt="logo"
        class="size-24 bg-transparent"
      />
    </div>
    <form
      action="#"
      autocomplete="off"
      onsubmit={(event) => {
        event.preventDefault()
        mutation.mutate(form.state.values)
      }}>
      <Card class="text-start border-none shadow-none md:border md:border-solid md:shadow!">
        <CardHeader class="pt-2 md:pt-4">
          <CardTitle class="text-2xl">Reset Kata Sandi</CardTitle>
          <CardDescription>
            Masukkan kata sandi baru untuk menyelesaikan proses reset.
          </CardDescription>
        </CardHeader>
        <CardContent class="grid gap-3">
          <form.Field name="email">
            {#snippet children(field)}
              <div>
                <FieldLabel>Email</FieldLabel>
                <Input
                  type="email"
                  name={field.name}
                  autocapitalize="off"
                  autocomplete="email"
                  placeholder="email"
                  value={field.state.value}
                  oninput={(event) => field.handleChange(event.currentTarget.value)}
                  disabled
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="password">
            {#snippet children(field)}
              {@const show = form.useSelector((state) => state.values.password_show)}
              <div>
                <div class="flex justify-between">
                  <FieldLabel>Kata Sandi Baru</FieldLabel>
                </div>
                <div class="relative">
                  <Input
                    type={show.current ? "text" : "password"}
                    name={field.name}
                    class="pe-9"
                    autocapitalize="off"
                    autocomplete="new-password"
                    value={field.state.value}
                    oninput={(event) => field.handleChange(event.currentTarget.value)}
                    placeholder="••••••••"
                  />
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    class="absolute top-0.5 inset-e-0.5 group"
                    data-show={show.current}
                    onclick={() => {
                      form.setFieldValue("password_show", !form.getFieldValue("password_show"))
                    }}>
                    <EyeOffIcon class="size-4 group-data-[show=true]:block hidden"/>
                    <EyeIcon class="size-4 group-data-[show=false]:block hidden"/>
                  </Button>
                </div>
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="password_confirm">
            {#snippet children(field)}
              <div>
                <FieldLabel>Kata Sandi Ulang</FieldLabel>
                <Input
                  type="password"
                  name={field.name}
                  autocapitalize="off"
                  autocomplete="new-password"
                  value={field.state.value}
                  oninput={(event) => field.handleChange(event.currentTarget.value)}
                  placeholder="••••••••"
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
        </CardContent>
        <CardFooter class="flex-col gap-2">
          <Button
            type="submit"
            class="w-full group"
            data-loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            <span class="group-data-[loading=true]:invisible">Simpan kata sandi</span>
            <LoaderCircleIcon class="group-data-[loading=true]:visible invisible absolute animate-spin"/>
          </Button>
        </CardFooter>
      </Card>
    </form>
  </div>
</main>
